import { UserFactory, EvaluationFactory } from "@/classes/abstractFactory";
import CurrentLessonService from "@/services/CurrentLessonService";
import evaluationTypeEnum from "@/enums/evaluationTypeEnum";

const state = {
  operationInProgress: false,
  status: null,
  isExam: false,
  lessonItems: [],
  teacher: {},
  videoLinks: [],
  nameLesson: {},
  courseName: {},
  lessonImages: [],
  evaluations: [],
  evaluationResult: 0,
  grade: "",
  currentStudentLessonId: "",
  chatId: null,
};

const getters = {
  getCurrentStudentLessonId(state) {
    return state.currentStudentLessonId;
  },
  isOperationInProgress(state) {
    return state.operationInProgress;
  },
  getGrade(state) {
    return state.grade;
  },
  hasValidationQuestions(state) {
    return state.evaluations.some((evaluation) =>
      [evaluationTypeEnum.TASK, evaluationTypeEnum.ADD_FILE].includes(
        evaluation.type
      )
    );
  },
  getEvaluationResult(state) {
    return state.evaluationResult;
  },
  isExam: (state) => {
    return state.isExam;
  },
  status() {
    return state.status;
  },
  getChatId(state) {
    return state.chatId;
  },
  getLessonText: (state) => {
    return state.lessonItems;
  },
  getTeacher: (state) => {
    return state.teacher;
  },
  getVideoLinks: (state) => {
    return state.videoLinks;
  },
  getNameLesson: (state) => {
    return state.nameLesson;
  },

  getCourseName: (state) => {
    return state.courseName;
  },
  getLessonImages: (state) => {
    return state.lessonImages;
  },
  getEvaluationDeck: (state) => {
    const evaluationViews = state.evaluations.map((evaluation) =>
      evaluation.createEvaluationView()
    );
    return EvaluationFactory.createEvaluationDeck(evaluationViews);
  },
};

const actions = {
  async getLesson({ commit, dispatch }, payload) {
    const { lessonId } = payload;
    const response = await CurrentLessonService.getCurrentLessonItems(lessonId);
    const status = response.data.status;
    const isExam = response.data.is_exam;
    const items = response.data.text;
    const teacher = response.data.teacher;
    const videoLinks = response.data.video_links;
    const nameLesson = response.data.name;
    const courseName = response.data.course_name;
    const lessonImages = response.data.images;
    const evaluations = response.data.evaluation;
    const evaluationResult = response.data.evaluation_result;
    const grade = response.data.grade || response.data.grade_number;
    let chatId = response.data.chat_room;
    const studentLessonId = response.data.student_lesson;
    if (!chatId) {
      const res = await dispatch(
        "ChatStore/createChat",
        {
          studentLessonId,
        },
        { root: true }
      );
      chatId = res.data.id;
    } else {
      await dispatch(
        "ChatStore/initChat",
        {
          chatId,
        },
        { root: true }
      );
    }

    commit("setStatus", status);
    commit("setIsExam", isExam);
    commit("setLesson", items);
    commit("setTeacher", teacher);
    commit("setVideoLinks", videoLinks);
    commit("setNameLesson", nameLesson);
    commit("setCourseName", courseName);
    commit("setLessonImages", lessonImages);
    commit("setEvaluations", evaluations);
    commit("setEvaluationResult", evaluationResult);
    commit("setChatId", chatId);
    commit("setGrade", grade);
    commit("setCurrentStudentLessonId", studentLessonId);
  },
  async changeLessonStatus({ commit }, payload) {
    const { lessonId } = payload;
    const { data } = await CurrentLessonService.changeLessonStatus(lessonId);
    commit("setStatus", data.status);
  },
  async submitTest({ commit }, payload) {
    let response = {};
    commit("setOperationInProgress", true);
    try {
      const { evaluations } = payload;
      response = await CurrentLessonService.submitTest(evaluations);
    } catch (error) {
      console.log(`Get error on submitTest error: ${error}`);
    }
    commit("setOperationInProgress", false);
    return response;
  },
  async submitOneTest({ commit }, payload) {
    let resp = false;
    commit("setOperationInProgress", true);
    try {
      const { evaluation } = payload;
      const { data } = await CurrentLessonService.submitOneTest(evaluation);
      resp =
        (data.hasOwnProperty("is_right_answer") && data.is_right_answer) ||
        data.is_right_answer === null;
    } catch (error) {
      console.log(`Get error on submitOneTest error: ${error}`);
    }
    commit("setOperationInProgress", false);
    return resp;
  },
};

function createEvaluation(rawEvaluation) {
  let evaluation;
  switch (rawEvaluation.type) {
    case 0: {
      const evaluationBuilder = EvaluationFactory.getEvaluationQuizBuilder();
      const answers = ["answer_1", "answer_2", "answer_3", "answer_4"].reduce(
        (answs, prop, index) => {
          const val = rawEvaluation[prop];
          if (val) {
            const ans = EvaluationFactory.createAnswer(val, index);
            answs.push(ans);
          }

          return answs;
        },
        []
      );
      evaluation = evaluationBuilder
        .setId(rawEvaluation.id)
        .setTask(rawEvaluation.task)
        .setType(evaluationTypeEnum.QUIZ)
        .setAnswers(answers)
        .setResults(rawEvaluation.results)
        .build();
      break;
    }
    case 1: {
      const evaluationBuilder = EvaluationFactory.getEvaluationTaskBuilder();
      evaluation = evaluationBuilder
        .setId(rawEvaluation.id)
        .setTask(rawEvaluation.task)
        .setType(evaluationTypeEnum.TASK)
        .setResults(rawEvaluation.results)
        .build();
      break;
    }
    case 2:
    case 3: {
      const evaluationBuilder = EvaluationFactory.getEvaluationAddFileBuilder();
      evaluation = evaluationBuilder
        .setId(rawEvaluation.id)
        .setTask(rawEvaluation.task)
        .setType(evaluationTypeEnum.ADD_FILE)
        .setResults(rawEvaluation.results)
        .build();
      break;
    }
    default: {
      console.error(`Unsupported evaluation type: ${rawEvaluation.type}`);
    }
  }
  return evaluation;
}

const mutations = {
  setCurrentStudentLessonId(state, val) {
    state.currentStudentLessonId = val;
  },
  setOperationInProgress(state, val) {
    state.operationInProgress = val;
  },
  setLesson(state, lessonItems) {
    state.lessonItems = lessonItems;
  },
  setTeacher(state, rawTeacher) {
    const builder = UserFactory.getUserBuilder();
    const teacher = builder
      .setFirstName(rawTeacher.first_name)
      .setLastName(rawTeacher.last_name)
      .setEmail(rawTeacher.email)
      .setUserId(rawTeacher.id)
      .seRole(rawTeacher.role)
      .build();
    state.teacher = teacher;
  },
  setVideoLinks(state, videoLinks) {
    state.videoLinks = videoLinks;
  },
  setNameLesson(state, nameLesson) {
    state.nameLesson = nameLesson;
  },
  setCourseName(state, courseName) {
    state.courseName = courseName;
  },
  setLessonImages(state, lessonImages) {
    state.lessonImages = lessonImages;
  },
  setIsExam(state, isExam) {
    state.isExam = isExam;
  },
  setStatus(state, status) {
    state.status = status;
  },
  setEvaluations(state, evaluations) {
    state.evaluations = evaluations.map((rawEvaluation) =>
      createEvaluation(rawEvaluation)
    );
  },
  setEvaluationResult(state, evaluationResult) {
    state.evaluationResult = evaluationResult || 0;
  },
  setChatId(state, chatId) {
    state.chatId = chatId || null;
  },
  setGrade(state, grade) {
    state.grade = grade || "";
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
